import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Signal,
} from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { CookieService } from "ngx-cookie-service";
import { filter, map, merge, Observable, Subject } from "rxjs";

import { BannerItem, BannersData } from "@hermes/api-model-shell";
import { Context, StorageManager, StorageService } from "@hermes/core-app-core";
import { COOKIEBANNER, ShellFacade } from "@hermes/domains-shell-states-shell-state";
import {
    ENABLE_NEW_GEOFILTERING_TRAY,
    ENABLE_NEW_NOTIFICATION_BANNER,
    FeatureFlagFacade,
} from "@hermes/hermes-web-states-flipper-state";

import { NOTIFICATIONSHIDE } from "../../constants/cookie.constants";
import { isBannerItemsNotEmpty } from "../../helpers/banner.helper";
import { CookieBannerComponent } from "../cookie-banner/cookie-banner.component";
import { NotificationBannerComponent } from "../notification-banner/notification-banner.component";

@Component({
    selector: "h-banners-top-container",
    imports: [CookieBannerComponent, NotificationBannerComponent, AsyncPipe],
    templateUrl: "./banners-top-container.component.html",
    styleUrls: ["./banners-top-container.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
  })
export class BannersTopContainerComponent {
    private context = inject(Context);
    private shellFacade = inject(ShellFacade);
    private cookieService = inject(CookieService);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private storageService = inject(StorageService);

    public cookieBanner$: Observable<BannerItem | undefined>;
    public notificationsBanners$: Observable<BannerItem[] | undefined>;
    public isNewGeofilteringTrayEnable: Signal<boolean>;
    public isNewNotificationBannerEnable: Signal<boolean>;

    private banners$: Observable<BannersData>;
    private onNotificationsBannerClosed$: Subject<void> = new Subject<void>();
    private onCookieBannerClosed$: Subject<void> = new Subject<void>();
    private sessionStorage: StorageManager | undefined;

    constructor() {
        this.isNewNotificationBannerEnable = toSignal(
            this.featureFlagFacade.isActivated(ENABLE_NEW_NOTIFICATION_BANNER),
            { initialValue: false },
        );

        this.isNewGeofilteringTrayEnable = toSignal(
            this.featureFlagFacade.isActivated(ENABLE_NEW_GEOFILTERING_TRAY),
            { initialValue: false },
        );

        this.sessionStorage = this.storageService.getSessionStorageInstance();
        this.banners$ = this.shellFacade.getBanners();
        this.notificationsBanners$ = this.banners$.pipe(
            map((banners) => banners.notifications),
        );

        this.cookieBanner$ = merge(
            this.banners$.pipe(
                map((banners) => banners.cookie),
                filter((cookieBanner) =>
                    this.shouldCookieBannerBeVisible(cookieBanner),
                ),
            ),
            this.onCookieBannerClosed$.pipe(map(() => undefined)),
        );

        this.notificationsBanners$ = merge(
            this.banners$.pipe(
                map((banners) => banners.notifications),
                filter(isBannerItemsNotEmpty),
            ),
            this.onNotificationsBannerClosed$.pipe(map(() => undefined)),
        );
    }

    public closeCookieBanner(): void {
        this.onCookieBannerClosed$.next();
    }

    public closeNotificationsBanner(): void {
        this.sessionStorage?.setItem(NOTIFICATIONSHIDE, true);
        this.onNotificationsBannerClosed$.next();
    }

    private shouldCookieBannerBeVisible(cookieBanner: BannerItem): boolean {
        if (cookieBanner) {
            const isActive = cookieBanner.active;
            const hasCookie = this.cookieService.get(COOKIEBANNER);
            const isNewerBanner =
                Number(hasCookie) < cookieBanner.cookieTimestamp;

            return (
                isActive &&
                (!hasCookie || isNewerBanner) &&
                this.context.isInBrowserMode()
            );
        }
        return false;
    }
}
